import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Clock } from 'lucide-react';
import { blogAPI } from '../services/api';
import { format } from 'date-fns';

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, [selectedCategory]);

  const fetchBlogs = async () => {
    try {
      const params = selectedCategory ? { category: selectedCategory } : {};
      const response = await blogAPI.getAll(params);
      // If no blogs in database, use default data
      if (response.data.blogs.length === 0) {
        setBlogs(defaultBlogs);
      } else {
        setBlogs(response.data.blogs);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setBlogs(defaultBlogs);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await blogAPI.getCategories();
      setCategories(response.data.categories.length > 0 ? response.data.categories : defaultCategories);
    } catch (error) {
      setCategories(defaultCategories);
    }
  };

  const defaultCategories = ['All', 'IT Development', 'ERP Solutions', 'IT Consulting', 'Digital Transformation', 'Industry Insights'];

  const defaultBlogs = [
    {
      _id: '1',
      slug: 'future-of-erp-ai-driven-insights',
      title: 'The Future of ERP: AI-Driven Insights for 2025',
      excerpt: 'Explore how artificial intelligence is transforming enterprise resource planning and what it means for your organization.',
      category: 'ERP Solutions',
      author: { name: 'TeqDeepSeek Team' },
      publishedAt: new Date().toISOString(),
      featuredImage: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&h=400&fit=crop',
      readTime: '5 min read'
    },
    {
      _id: '2',
      slug: 'cloud-migration-best-practices',
      title: 'Cloud Migration Best Practices for Dubai Enterprises',
      excerpt: 'A comprehensive guide to planning and executing successful cloud migrations while maintaining compliance with UAE regulations.',
      category: 'IT Consulting',
      author: { name: 'TeqDeepSeek Team' },
      publishedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      featuredImage: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=400&fit=crop',
      readTime: '8 min read'
    },
    {
      _id: '3',
      slug: 'digital-transformation-healthcare',
      title: 'Digital Transformation in Healthcare: Lessons from the Field',
      excerpt: 'Real-world insights from our healthcare digital transformation projects and the strategies that drive success.',
      category: 'Industry Insights',
      author: { name: 'TeqDeepSeek Team' },
      publishedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      featuredImage: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=400&fit=crop',
      readTime: '6 min read'
    },
    {
      _id: '4',
      slug: 'staff-augmentment-vs-dedicated-teams',
      title: 'Staff Augmentation vs Dedicated Teams: Choosing the Right Model',
      excerpt: 'Understanding the differences between these two engagement models and when to use each for maximum impact.',
      category: 'IT Development',
      author: { name: 'TeqDeepSeek Team' },
      publishedAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
      featuredImage: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop',
      readTime: '4 min read'
    },
    {
      _id: '5',
      slug: 'oracle-vs-sap-which-erp-right',
      title: 'Oracle vs SAP: Which ERP is Right for Your Business?',
      excerpt: 'A detailed comparison of two leading ERP platforms to help you make an informed decision.',
      category: 'ERP Solutions',
      author: { name: 'TeqDeepSeek Team' },
      publishedAt: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
      featuredImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=400&fit=crop',
      readTime: '7 min read'
    },
    {
      _id: '6',
      slug: 'cybersecurity-enterprise-applications',
      title: 'Cybersecurity Best Practices for Enterprise Applications',
      excerpt: 'Essential security measures to protect your business-critical applications and data.',
      category: 'IT Consulting',
      author: { name: 'TeqDeepSeek Team' },
      publishedAt: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString(),
      featuredImage: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&h=400&fit=crop',
      readTime: '6 min read'
    }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 gradient-hero">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">Blog</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Insights & Resources
            </h1>
            <p className="text-lg text-white/80">
              Stay updated with the latest trends, best practices, and insights 
              from the world of enterprise technology.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap gap-3 mb-12"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category === 'All' ? '' : category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  (category === 'All' && !selectedCategory) || category === selectedCategory
                    ? 'bg-accent text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </motion.div>

          {/* Blog Grid */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {blogs.map((blog) => (
              <motion.article
                key={blog._id}
                variants={fadeInUp}
                className="card-hover overflow-hidden group"
              >
                <Link to={`/blog/${blog.slug}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={blog.featuredImage || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&h=400&fit=crop'}
                      alt={blog.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-accent text-white text-xs font-medium rounded-full">
                        {blog.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {format(new Date(blog.publishedAt), 'MMM d, yyyy')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {blog.readTime || '5 min read'}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-primary mb-3 group-hover:text-accent transition-colors line-clamp-2">
                      {blog.title}
                    </h3>
                    
                    <p className="text-slate-600 text-sm line-clamp-2 mb-4">
                      {blog.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm text-slate-500">
                        <User className="w-4 h-4" />
                        {blog.author?.name || 'TeqDeepSeek Team'}
                      </span>
                      <span className="text-accent font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read More
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </motion.div>

          {/* Load More */}
          <div className="text-center mt-12">
            <button className="btn-secondary">
              Load More Articles
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="section bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-primary mb-4">
              Subscribe to Our Newsletter
            </h2>
            <p className="text-slate-600 mb-8">
              Get the latest articles, insights, and updates delivered directly to your inbox.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 input"
              />
              <button type="submit" className="btn-primary whitespace-nowrap">
                Subscribe
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
